"use client";

import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { Link } from "@/i18n/navigation";

/**
 * What the landing's "try it" buttons do.
 *
 * There are six of them — nav (desktop and mobile), hero, pricing (two plans)
 * and the closing CTA — and on getmintai they must open the onboarding tour
 * instead of jumping straight to sign-up. Routing that through one context
 * keeps the decision in `landing-page.tsx`, where the tour's state already
 * lives, rather than teaching six components about brand modes.
 */
interface SignupFlow {
  /** Destination, or null when the button opens the tour instead of navigating. */
  href: string | null;
  /** Opens the tour. No-op when `href` is set. */
  start: () => void;
}

const SignupFlowContext = createContext<SignupFlow>({
  href: "/sign-up",
  start: () => {},
});

export function SignupFlowProvider({
  value,
  children,
}: {
  value: SignupFlow;
  children: ReactNode;
}) {
  return (
    <SignupFlowContext.Provider value={value}>
      {children}
    </SignupFlowContext.Provider>
  );
}

export function useSignupFlow(): SignupFlow {
  return useContext(SignupFlowContext);
}

/**
 * A landing CTA: renders a link when there is somewhere to go, a button when
 * the press opens the tour. Same markup either way, so the styling each call
 * site already had keeps working.
 */
export function SignupCta({
  className,
  style,
  onPress,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  /** Analytics and local state — runs before navigating or opening the tour. */
  onPress?: () => void;
  children: ReactNode;
}) {
  const { href, start } = useSignupFlow();

  if (href) {
    return (
      <Link href={href} onClick={onPress} className={className} style={style}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        onPress?.();
        start();
      }}
      className={className}
      style={style}
    >
      {children}
    </button>
  );
}
